import React, { FC, useEffect, useMemo } from 'react';

import { Grid } from '@mui/material';
import { Box } from '@mui/system';
import { FormikProps } from 'formik';

import {
  getPharmacistDetailsSearchFilters,
  getPublishedSoftwareList,
  ProfessionalOnlyUsers,
  publishedSoftwareList,
} from '@pharmaplan/common';
import { dropdownMapper } from '@pharmaplan/common/helpers/utils';
import { idStore } from '@pharmaplan/common/helpers/ServiceHandler';

import useProfessionalListConfig from '../../../../hooks/PharmacistList/useProfessionalListConfig';
import useDynamicProperties from '../../../../hooks/useDynamicProperties';
import FilterSubmitButtons from '../../../common/FilterSubmitButtons';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import { UserListTabTypes } from '../../../../helpers/Constants';
import CustomTextField from '../../../common/CustomTextField';
import genericClasses from '../../../../theme/GenericClasses';
import CustomDropdown from '../../../common/CustomDropdown';
import strings from '../../../../localization';
import PendingTabs from '../../PendingTabs';

import styles from './styles';

interface IPharmacistListHeader {
  selectedTab: UserListTabTypes;
  handleTabClick: (tab: UserListTabTypes) => void;
  formik: FormikProps<any>;
  handleReset: () => void;
  userType: ProfessionalOnlyUsers;
}

const PharmacistListHeader: FC<IPharmacistListHeader> = ({
  selectedTab,
  handleTabClick,
  formik,
  handleReset,
  userType,
}) => {
  const dispatch = useAppDispatch();

  const { filterCapacity } = useDynamicProperties();
  const softwares = useAppSelector(publishedSoftwareList);

  const { userLabel } = useProfessionalListConfig(userType);
  const isDetailsSearch = selectedTab === UserListTabTypes.detailsSearch;

  const tabConfig = [
    {
      key: UserListTabTypes.active,
      isDefault: true,
      tabName: strings.formatString(
        strings.activeUsersFormat,
        userLabel,
      ) as string,
      onClick: handleTabClick,
    },
    {
      key: UserListTabTypes.deactivated,
      tabName: strings.formatString(
        strings.deactivatedUsersFormat,
        userLabel,
      ) as string,
      onClick: handleTabClick,
    },
    {
      key: UserListTabTypes.hidden,
      tabName: strings.formatString(
        strings.hiddenUsersFormat,
        userLabel,
      ) as string,
      onClick: handleTabClick,
    },
    {
      key: UserListTabTypes.deleted,
      tabName: strings.userDeletedAccounts,
      onClick: handleTabClick,
    },
    {
      key: UserListTabTypes.detailsSearch,
      tabName: strings.detailsSearch,
      onClick: handleTabClick,
    },
  ];

  useEffect(() => {
    if (isDetailsSearch) {
      dispatch(getPharmacistDetailsSearchFilters());
      dispatch(getPublishedSoftwareList());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab]);

  const pharmacistCapacitiesItems = useMemo(
    () =>
      filterCapacity.map((item) => {
        const capacityId = item[
          idStore.capacityId as keyof typeof item
        ] as string;
        const { from, to } = item;
        return {
          label: `${from}-${to}`,
          key: capacityId,
        };
      }),
    [filterCapacity],
  );

  return (
    <Box component="form" onSubmit={formik.handleSubmit}>
      <PendingTabs
        selectedTab={selectedTab}
        tabConfig={tabConfig}
        header={
          strings.formatString(strings.userListFormat, userLabel) as string
        }
      />
      <Grid container lg={5} alignItems="center" xs={7} wrap="nowrap">
        {isDetailsSearch ? (
          <>
            <Grid item xs={6}>
              <CustomDropdown
                label={strings.software}
                name="softwareIds"
                labelStyles={styles.softwareMenuStyles}
                id="softwareIds"
                formik={formik}
                multiple
                menuItems={dropdownMapper(
                  softwares,
                  'softwareId',
                  'description',
                )}
              />
            </Grid>
            <Grid item xs={6} sx={genericClasses.filterMargin}>
              <CustomDropdown
                label={strings.capacityFrom}
                name="capacityIds"
                customMenuStyles={styles.capacityMenuStyles}
                id="capacityIds"
                multiple
                formik={formik}
                menuItems={pharmacistCapacitiesItems}
              />
            </Grid>
          </>
        ) : (
          <Grid item xs={6}>
            <CustomTextField
              label={strings.user}
              placeholder={strings.enterNameEmail}
              name="userText"
              id="userText"
              formik={formik}
            />
          </Grid>
        )}
        <FilterSubmitButtons handleReset={handleReset} />
      </Grid>
    </Box>
  );
};

export default PharmacistListHeader;
